import type { Theme } from '@mui/material'
import { Alert, Box, Button, Divider, LinearProgress, Link, Stack, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ApplicationTypes } from 'element-domain'
import { useCallback } from 'react'

import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { commitPopOrNavigateToSummary } from '../../../../systems/commit-pop-or-summary'
import { configuratorInterface } from '../../../../systems/configurator-interface'

interface QuoteSuccessDialogContentProps {
  finalizationUuid: string
  onClose?: () => void
}

export const QuoteSuccessDialogContent = ({ finalizationUuid, onClose }: QuoteSuccessDialogContentProps) => {
  const { navigationSystem, storeSystem, configurationId } = configuratorInterface.useDomain()
  const apiClient = configuratorInterface.useAuthorizedClient()

  const getResult = useCallback(async () => {
    return apiClient.waitForFinalizationToComplete(configurationId, finalizationUuid, {
      pollingTimeout: 20 * 60 * 1000,
    })
  }, [apiClient, configurationId, finalizationUuid])

  const { data, isError } = useQuery({
    queryKey: ['finalize', finalizationUuid],
    queryFn: getResult,
  })

  const handleBack = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      commitPopOrNavigateToSummary(navigationSystem, storeSystem, ApplicationTypes.NavigationState.Summary)
    }
  }, [onClose, navigationSystem, storeSystem])

  return (
    <Dialog.ContentLayout
      controlsPosition="end"
      header={
        <Box
          component="div"
          sx={(theme: Theme) => ({
            backgroundColor: theme.palette.background.default,
            blockSize: 180,
            color: theme.palette.success.main,
            display: 'grid',
            placeItems: 'center',
          })}
        >
          <PhxIcon name="CheckCircle" style={{ fontSize: 80 }} />
        </Box>
      }
      body={
        <Stack sx={{ gap: 2, px: 1 }}>
          <Typography variant="h6">Quote request submitted</Typography>
          <Typography>
            We will email you the quote PDF, a preview image of your configuration, and links to review or share it.
          </Typography>

          <Divider />

          {isError && (
            <Alert severity="error">
              <b>Configuration resources could not be generated.</b>
              <br />
              Please try again later. If the issue persists, contact base.
            </Alert>
          )}
          {!data && (
            <>
              <Typography>Waiting for resources to be generated...</Typography>
              <LinearProgress />
            </>
          )}
          {data && (data.pdfLink || data.clientGeneratedImageLink) && (
            <>
              <Typography variant="subtitle1">Generated:</Typography>
              <Stack direction="row" sx={{ gap: 2 }}>
                {data.pdfLink && (
                  <Link href={data.pdfLink} target="_blank">
                    Details PDF
                  </Link>
                )}
                {data.clientGeneratedImageLink && (
                  <Link href={data.clientGeneratedImageLink} target="_blank">
                    Preview Image
                  </Link>
                )}
              </Stack>
            </>
          )}
        </Stack>
      }
      controls={
        <Button variant="text" onClick={handleBack} startIcon={<PhxIcon name="ArrowLeft" />}>
          Back to Summary
        </Button>
      }
    />
  )
}
